import { User, Target, AlertCircle, Phone, Search } from "lucide-react"
import { cn } from "@/utils/cn"
import { OCUPACAO_COLORS } from "./constants"
import { fieldClass, FormField } from "./formField"
import type { BdrLeadFormProps } from "./types"
import type { useBdrLeadFormCompany } from "./useBdrLeadFormCompany"

type CompanyHook = ReturnType<typeof useBdrLeadFormCompany>

export function BdrLeadFormEssentials(props: BdrLeadFormProps & { company: CompanyHook }) {
  const { newLead, backendErrors, mode, company } = props
  const {
    companySearch, setCompanySearch, isOpen, setIsOpen, options, loading, ocupacao, setOcupacao,
    contatosEmpresa, wrapperRef, updateField, handleSelectCompany, handleSelectContact, formatPhone,
  } = company

  return (
    <>
      {/* ── Seção Superior: Campos Obrigatórios (Destaque) ────────────────── */}
      <section className="lg:col-span-2 bg-gradient-to-br from-amber-500/[0.04] to-accent/[0.04] border border-amber-500/20 rounded-2xl p-6 space-y-6 shadow-xl relative">
        {/* Glow decoration */}
        <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-36 h-36 bg-amber-500/[0.03] rounded-full blur-3xl" />
        </div>

        <div className="flex items-center justify-between border-b border-amber-500/10 pb-4 relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/10 rounded-xl border border-amber-500/30 text-amber-400">
              <Target size={18} className="animate-pulse" />
            </div>
            <div>
              <h3 className="text-amber-400 font-bold text-sm uppercase tracking-wide">
                Informações Essenciais
              </h3>
              <p className="text-[10px] text-text-muted mt-0.5">
                Preencha os 3 campos obrigatórios abaixo para ativar o cadastro
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping" />
            <span className="text-[10px] font-black text-amber-400 uppercase tracking-widest">
              Prioritário
            </span>
          </div>
        </div>

        {/* Occupancy Warning */}
        {ocupacao && (
          <div 
            className="flex items-center gap-3 p-4 rounded-xl border transition-all duration-300 animate-fadeIn relative z-10"
            style={{
              backgroundColor: OCUPACAO_COLORS[ocupacao.tipo]?.bg || 'rgba(251, 191, 36, 0.08)',
              borderColor: OCUPACAO_COLORS[ocupacao.tipo]?.border || 'rgba(251, 191, 36, 0.3)',
              color: OCUPACAO_COLORS[ocupacao.tipo]?.text || '#fbbf24'
            }}
          >
            <AlertCircle size={18} />
            <div className="flex-1">
              <strong className="text-xs font-bold block uppercase tracking-wide">
                {ocupacao.tipo === 'deal' || ocupacao.tipo === 'closer' ? 'Restrição de Entrada' : 'Aviso de Prospecção'}
              </strong>
              <span className="text-[11px] opacity-90 block mt-0.5">
                {ocupacao.label}
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
          {/* 1. Empresa Autocomplete Field */}
          <div
            className="relative"
            ref={wrapperRef}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setIsOpen(false)
              }
            }}
          >
            <FormField
              id="empresa-input"
              label="1. Empresa"
              required
              error={backendErrors.empresa}
            >
              <div className="relative">
                <input
                  id="empresa-input"
                  type="text"
                  value={companySearch}
                  onChange={e => {
                    setCompanySearch(e.target.value)
                    updateField('empresa', e.target.value)
                    setIsOpen(true)
                    if (e.target.value.trim() === '') {
                      updateField('empresaId', undefined)
                      setOcupacao(null)
                    }
                  }}
                  onFocus={() => setIsOpen(true)}
                  onBlur={() => {
                    window.setTimeout(() => {
                      if (wrapperRef.current?.contains(document.activeElement)) return
                      setIsOpen(false)
                    }, 0)
                  }}
                  placeholder={mode === 'create' ? "Buscar ou digitar empresa" : "Editar empresa"}
                  className={cn(fieldClass(backendErrors.empresa, true), "border-2 border-amber-500/20 focus:border-amber-500 bg-amber-500/[0.01]")}
                  autoComplete="off"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {loading ? (
                    <div className="w-3.5 h-3.5 border-2 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
                  ) : (
                    <Search size={15} className="text-amber-500/60" />
                  )}
                </div>
              </div>
            </FormField>

            {/* Autocomplete Dropdown Portal style */}
            {isOpen && options.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 z-50 max-h-56 overflow-y-auto scrollbar-thin rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] shadow-2xl">
                <div className="p-1">
                  {options.map((emp) => (
                      <button
                        key={emp.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelectCompany(emp)}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-[var(--glass-bg)] transition-colors flex items-center justify-between group"
                      >
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-bold text-text-primary group-hover:text-amber-400 transition-colors">
                            {emp.nome}
                          </span>
                          <span className="text-[10px] text-text-muted">
                            {emp.segmento || 'Sem segmento'} {emp.cidade ? `• ${emp.cidade}` : ''}
                          </span>
                        </div>
                        {emp.ocupacao && (
                          <span 
                            className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border"
                            style={{
                              backgroundColor: OCUPACAO_COLORS[emp.ocupacao.tipo]?.bg,
                              borderColor: OCUPACAO_COLORS[emp.ocupacao.tipo]?.border,
                              color: OCUPACAO_COLORS[emp.ocupacao.tipo]?.text
                            }}
                          >
                            {OCUPACAO_COLORS[emp.ocupacao.tipo]?.label}
                          </span>
                        )}
                      </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 2. Nome do Contato */}
          <FormField
            id="contato-input"
            label="2. Nome do Contato"
            required
            error={backendErrors.contato}
          >
            <div className="relative">
              <input
                id="contato-input"
                type="text"
                value={newLead.contato || ''}
                onChange={e => updateField('contato', e.target.value)}
                placeholder="Nome do decisor"
                className={cn(fieldClass(backendErrors.contato, true), "border-2 border-amber-500/20 focus:border-amber-500 bg-amber-500/[0.01]")}
              />
              <User size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-amber-500/60" />
            </div>
          </FormField>

          {/* 3. Telefone */}
          <FormField
            id="telefone-input"
            label="3. Telefone"
            required
            error={backendErrors.telefone}
          >
            <div className="relative">
              <input
                id="telefone-input"
                type="tel"
                value={newLead.telefone || ''}
                onChange={e => updateField('telefone', formatPhone(e.target.value))}
                placeholder="(00) 00000-0000"
                className={cn(fieldClass(backendErrors.telefone, true), "border-2 border-amber-500/20 focus:border-amber-500 bg-amber-500/[0.01]")}
              />
              <Phone size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-amber-500/60" />
            </div>
          </FormField>
        </div>

        {/* Existing Contacts Quick Fill */}
        {contatosEmpresa.length > 0 && (
          <div className="pt-2 border-t border-amber-500/10 animate-fadeIn relative z-10">
            <span className="text-[10px] text-text-muted font-bold block uppercase tracking-wider mb-2">
              Contatos existentes nesta empresa (Clique para preencher):
            </span>
            <div className="flex flex-wrap gap-2">
              {contatosEmpresa.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleSelectContact(c)}
                  className="px-3 py-1.5 bg-[#0f172a] hover:bg-amber-500/10 border border-white/[0.06] hover:border-amber-500/30 rounded-lg text-xs font-bold text-text-secondary hover:text-amber-400 flex items-center gap-1.5 transition-all"
                >
                  <User size={12} />
                  {c.nome} {c.cargo ? `(${c.cargo})` : ''}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  )
}
